import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ApiError, api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { Button } from "@/components/ui/button";

type Status = "idle" | "verifying" | "success" | "error";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { user, refreshMe, resendVerification } = useAuth();

  const [status, setStatus] = useState<Status>(token ? "verifying" : "error");
  const [message, setMessage] = useState<string | null>(
    token ? null : "No verification token in the link."
  );
  const [resending, setResending] = useState(false);
  const verifyOnceRef = useRef(false);

  useEffect(() => {
    if (!token) return;
    // StrictMode double-invokes effects in dev; ensure the verify POST fires
    // exactly once per mount so the second call doesn't see the token consumed.
    if (verifyOnceRef.current) return;
    verifyOnceRef.current = true;
    (async () => {
      try {
        await api.post("/auth/verify-email", { token }, { auth: false });
        setStatus("success");
        setMessage("Your email is verified. Welcome aboard.");
        if (user) {
          try {
            await refreshMe();
          } catch {
            /* ignore */
          }
        }
      } catch (e) {
        setStatus("error");
        setMessage(
          e instanceof ApiError ? e.message : "Verification failed."
        );
      }
    })();
  }, [token, user, refreshMe]);

  async function onResend() {
    setResending(true);
    try {
      await resendVerification();
      setMessage("A new verification link has been sent.");
    } catch (e) {
      setMessage(e instanceof ApiError ? e.message : "Could not resend.");
    } finally {
      setResending(false);
    }
  }

  return (
    <AuthLayout
      testId="verify-email-page"
      eyebrow="Email verification"
      heading={
        status === "success"
          ? "You're verified."
          : status === "verifying"
          ? "Verifying your email…"
          : "We couldn't verify that link."
      }
      description={message}
    >
      <div className="space-y-4">
        {status === "success" && (
          <Button
            asChild
            data-testid="verify-cta-app"
            className="w-full bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-12"
            onClick={() => navigate("/app")}
          >
            <Link to={user ? "/app" : "/login"}>
              {user ? "Go to dashboard" : "Sign in"}
            </Link>
          </Button>
        )}
        {status === "error" && user && (
          <Button
            data-testid="verify-resend"
            onClick={onResend}
            disabled={resending}
            className="w-full bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-12"
          >
            {resending ? "Sending…" : "Resend verification email"}
          </Button>
        )}
        {status === "error" && !user && (
          <Button
            asChild
            className="w-full bg-foreground hover:bg-charcoal text-ivory rounded-none h-12"
          >
            <Link to="/login">Sign in to resend</Link>
          </Button>
        )}
      </div>
    </AuthLayout>
  );
}
